import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormGroup,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import FormNumberField from "../../components/formComponents/FormNumberField";
import FormSwitch from "../../components/formComponents/FormSwitch";
import { CUSTOMER_STATUS_VALUES } from "../../customers/customerStatus";
import type { CustomersAutomationConfig } from "./automationConfigApi";
import { useCustomerAutomationConfig } from "./useCustomerAutomationConfig";

export type OrderSettingsFormState = CustomersAutomationConfig;

const DescriptionDialog: FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle>Reguły automatyzacji</DialogTitle>
    <DialogContent>
      <Typography variant="subtitle2">Przypominaj przedstawicielowi o kontakcie</Typography>
      <Typography>{"Data kontaktu < dziś"}</Typography>
      <Typography variant="subtitle2" sx={{ marginTop: 2 }}>
        Automatycznie odpinaj opiekuna przy braku kontaktu
      </Typography>
      <Typography>
        Jeśli opiekun klienta nie stworzył żadnej notatki typu mail/telefon przez więcej niż X dni, lub żadnej notatki
        typu spotkanie online/spotkanie w biurze przez więcej niż Y dni (licząc od dziś), opiekun klienta zostanie
        zmieniony na &quot;Nieprzypisane biura&quot;. Wartości X i Y ustawiane są osobno dla każdego statusu klienta
        poniżej.
      </Typography>
      <Typography variant="body2" sx={{ marginTop: 2 }}>
        Proces uruchamiany jest w każdy wtorek o 11:00
      </Typography>
    </DialogContent>
  </Dialog>
);

const AccountManagerThresholdsTable: FC<{ control: ReturnType<typeof useForm<OrderSettingsFormState>>["control"] }> = ({
  control,
}) => (
  <Table size="small" sx={{ marginTop: 2 }}>
    <TableHead>
      <TableRow>
        <TableCell>Status klienta</TableCell>
        <TableCell>Brak maila/telefonu (dni)</TableCell>
        <TableCell>Brak spotkania (dni)</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {CUSTOMER_STATUS_VALUES.map((status) => (
        <TableRow key={status}>
          <TableCell>{status}</TableCell>
          <TableCell>
            <FormNumberField
              control={control}
              size="small"
              name={`accountManagerThresholds.${status}.contactThresholdDays`}
              label="Dni"
              min={1}
              max={365}
            />
          </TableCell>
          <TableCell>
            <FormNumberField
              control={control}
              size="small"
              name={`accountManagerThresholds.${status}.meetingThresholdDays`}
              label="Dni"
              min={1}
              max={365}
            />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const CustomersSettings: FC<{
  value: CustomersAutomationConfig;
  onSubmit: (config: CustomersAutomationConfig) => Promise<CustomersAutomationConfig>;
}> = ({ value, onSubmit }) => {
  const { control, handleSubmit, reset } = useForm<OrderSettingsFormState>({
    defaultValues: value,
  });
  const [showDescription, setShowDescription] = useState(false);

  return (
    <Card>
      <CardHeader title="Klienci" />
      <CardContent>
        <FormControl component="fieldset" variant="standard">
          <FormGroup>
            <FormSwitch
              control={control}
              name="contactRequiredNotification"
              label="Przypominaj przedstawicielowi o kontakcie"
            />
            <FormSwitch
              control={control}
              name="accountManagerReassignmentEnabled"
              label="Automatycznie odpinaj opiekuna przy braku kontaktu"
            />
          </FormGroup>
        </FormControl>
        <AccountManagerThresholdsTable control={control} />
      </CardContent>
      <CardActions>
        <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end", width: "100%" }}>
          <CancelButton onClick={() => reset()} />
          <Button variant="outlined" color="info" onClick={() => setShowDescription(true)}>
            Zobacz reguły
          </Button>
          <AsyncLoadingButton
            onClick={handleSubmit(async (s) => {
              await onSubmit(s);
            })}
          >
            Zapisz
          </AsyncLoadingButton>
        </Stack>
        <DescriptionDialog open={showDescription} onClose={() => setShowDescription(false)} />
      </CardActions>
    </Card>
  );
};

const CustomerAutomations = () => {
  const { isLoading, data, updateSettings } = useCustomerAutomationConfig();
  if (isLoading || !data) {
    return (
      <Card>
        <CardHeader title="Klienci" />
        <CardContent>
          <LinearProgress data-testid="loading-indicator" />
        </CardContent>
      </Card>
    );
  }
  return <CustomersSettings value={data} onSubmit={updateSettings} />;
};

export default CustomerAutomations;
